// ── App Shell — orchestrates everything ──

import { useEffect, useState, useCallback, useMemo, useRef, lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router";
import GraphScene from "./components/GraphScene";
import DetailPanel from "./components/DetailPanel";
import FilterBar from "./components/FilterBar";
import LoadingScreen from "./components/LoadingScreen";
import ViewSwitcher from "./components/ViewSwitcher";
import LandingPage from "./pages/LandingPage";
import { useWhoAmI } from "./hooks/useOpsApi";
import type { ViewMode } from "./components/ViewSwitcher";
import HierarchyView from "./components/HierarchyView";
import SiteSelector from "./components/SiteSelector";
import { useSites, useFullGraph } from "./hooks/useGraphData";
import { useNodeExpand } from "./hooks/useNodeExpand";
import { inspectionMatchesFacetFilters } from "./utils/graph-facet-filters";
import type { FilterState, GraphNode, NodeType } from "./types/graph";

// Lazy-load heavy pages
const Graph3DScene = lazy(() => import("./components/Graph3DScene"));
const OpsLayout = lazy(() => import("./pages/ops/OpsLayout"));
const OpsDashboard = lazy(() => import("./pages/ops/OpsDashboard"));
const OpsGrid = lazy(() => import("./pages/ops/OpsGrid"));
const OpsRecordDetail = lazy(() => import("./pages/ops/OpsRecordDetail"));
const OpsInspectionGrid = lazy(() => import("./pages/ops/OpsInspectionGridRouter"));
const OpsInspectionFullGrid = lazy(() => import("./pages/ops/OpsInspectionFullGridRouter"));
const OpsInspectionDetail = lazy(() => import("./pages/ops/OpsInspectionDetail"));
const MethodologiesDashboard = lazy(() => import("./pages/ops/methodologies/MethodologiesDashboard"));
const MethodologyWizard = lazy(() => import("./pages/ops/methodologies/MethodologyWizard"));
const MethodologySeriesDetail = lazy(() => import("./pages/ops/methodologies/MethodologySeriesDetail"));
const MethodologyRevisionWorkspace = lazy(() => import("./pages/ops/methodologies/MethodologyRevisionWorkspace"));

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

/** Wrapper that forces OpsInspectionGrid to remount when route params change (resets filter state) */
function OpsInspectionGridKeyed() {
  const { siteId, viewName } = useParams();
  return <OpsInspectionGrid key={`${siteId}/${viewName}`} />;
}

function AppInner() {
  const { data: sites, isLoading: sitesLoading } = useSites();
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);

  // Default to first site once loaded (if nothing selected yet)
  useEffect(() => {
    if (sites?.length && !selectedSiteId) {
      setSelectedSiteId(sites[0].id);
    }
  }, [sites, selectedSiteId]);

  const { data: fullGraph, isLoading: fullGraphLoading } = useFullGraph(selectedSiteId);
  const {
    graphData,
    selectedNode,
    setSelectedNode,
    onNodeClick,
    initFullGraph,
    expandAll,
    retractOneLevel,
    expandNode,
  } = useNodeExpand();

  const [filter, setFilter] = useState<FilterState>({});
  const [activeView, setActiveView] = useState<ViewMode>("network");
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [breadcrumb, setBreadcrumb] = useState<
    Array<{ label: string; type: NodeType }>
  >([]);

  // Window resize
  useEffect(() => {
    const onResize = () =>
      setDimensions({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Init full graph when loaded (single-query replaces sequential expandAll waterfall)
  const expandedOnce = useRef(false);
  useEffect(() => {
    if (fullGraph && !expandedOnce.current) {
      initFullGraph(fullGraph);
      expandedOnce.current = true;
    }
  }, [fullGraph, initFullGraph]);

  // Reset graph when site changes
  useEffect(() => {
    expandedOnce.current = false;
  }, [selectedSiteId]);

  // Track breadcrumb
  const handleNodeClick = useCallback(
    async (node: GraphNode) => {
      await onNodeClick(node);

      // Update breadcrumb based on node type
      if (node.type === "site") {
        setBreadcrumb([{ label: node.label, type: node.type }]);
      } else if (node.type === "campaign") {
        setBreadcrumb((prev) => [
          prev[0] ?? { label: "?", type: "site" as NodeType },
          { label: node.label, type: node.type },
        ]);
      } else if (node.type === "area") {
        setBreadcrumb((prev) => [
          ...prev.slice(0, 2),
          { label: node.label, type: node.type },
        ]);
      }
    },
    [onNodeClick],
  );

  // Apply filters to graph data
  const filteredData = useMemo(() => {
    const hideTypes = new Set(filter.hideTypes ?? []);
    const minSev = filter.minSeverity ?? 0;
    const facetBindings = fullGraph?.facetFilters ?? [];

    const nodes = graphData.nodes.filter((n) => {
      if (hideTypes.has(n.type)) return false;
      if (minSev > 0 && n.severity !== undefined && n.severity < minSev) return false;
      if (!inspectionMatchesFacetFilters(n, filter, facetBindings)) return false;
      return true;
    });

    const nodeIds = new Set(nodes.map((n) => n.id));
    const links = graphData.links.filter((l) => {
      const src = typeof l.source === "string" ? l.source : (l.source as unknown as GraphNode).id;
      const tgt = typeof l.target === "string" ? l.target : (l.target as unknown as GraphNode).id;
      return nodeIds.has(src) && nodeIds.has(tgt);
    });

    return { nodes, links };
  }, [graphData, filter, fullGraph?.facetFilters]);

  return (
    <div className="relative w-screen h-screen overflow-hidden bg-white">
      {/* Filter bar */}
      <FilterBar
        filter={filter}
        onChange={setFilter}
        facets={fullGraph?.facets}
        facetFilters={fullGraph?.facetFilters}
        breadcrumb={breadcrumb}
        onBreadcrumbClick={(index) => {
          if (index < 0) setBreadcrumb([]);
          else setBreadcrumb((prev) => prev.slice(0, index + 1));
        }}
      >
        {/* Site selector dropdown */}
        <SiteSelector
          sites={sites ?? []}
          selectedSiteId={selectedSiteId}
          onSelect={setSelectedSiteId}
          loading={sitesLoading}
        />
        {/* View Switcher — rendered inside FilterBar via children */}
        <ViewSwitcher active={activeView} onChange={setActiveView} />
      </FilterBar>

      {/* ── Views — conditionally rendered ── */}
      {activeView === "network" && (
        <GraphScene
          nodes={filteredData.nodes}
          links={filteredData.links}
          onNodeClick={handleNodeClick}
          width={dimensions.width}
          height={dimensions.height}
          onExpandAll={expandAll}
          onRetractOneLevel={retractOneLevel}
          onExpandSelected={selectedNode && selectedNode.type !== "inspection"
            ? () => expandNode(selectedNode)
            : undefined
          }
        />
      )}

      {activeView === "hierarchy" && (
        <HierarchyView
          graphData={filteredData}
          onNodeClick={handleNodeClick}
          selectedNodeId={selectedNode?.id ?? null}
        />
      )}

      {activeView === "3d" && (
        <Suspense fallback={<LoadingScreen message="Loading 3D engine…" />}>
          <Graph3DScene
            graphData={filteredData}
            onNodeClick={handleNodeClick}
            selectedNodeId={selectedNode?.id ?? null}
            width={dimensions.width}
            height={dimensions.height}
          />
        </Suspense>
      )}

      {/* Detail Panel */}
      <DetailPanel
        node={selectedNode}
        graphData={graphData}
        onClose={() => setSelectedNode(null)}
      />

      {/* Loading — show overlay while fetching graph data */}
      {(sitesLoading || fullGraphLoading) && <LoadingScreen message="Connecting to graph…" />}

      {/* Bottom info bar */}
      <div
        className="fixed bottom-0 left-0 right-0 z-20 h-8 flex items-center justify-between px-4
                    bg-fl-sand-20/80 backdrop-blur-sm border-t border-fl-evergreen-20 text-xs text-fl-evergreen-60"
      >
        <span>
          {filteredData.nodes.length} nodes · {filteredData.links.length} links
        </span>
        <span>Click nodes to drill down · Scroll to zoom · Drag to pan</span>
      </div>
    </div>
  );
}

/** Graph explorer — the original single-page view */
function GraphView() {
  const whoAmIQuery = useWhoAmI();
  const isAdmin = whoAmIQuery.data?.user?.permissions.isAdmin ?? false;

  if (whoAmIQuery.isLoading) {
    return <LoadingScreen message="Checking graph access…" />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <AppInner />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Default landing page */}
          <Route path="/" element={<LandingPage />} />

          {/* Graph explorer (moved from /) */}
          <Route path="/graph" element={<GraphView />} />

          {/* Operations portal */}
          <Route
            path="/ops"
            element={
              <Suspense fallback={<LoadingScreen message="Loading portal…" />}>
                <OpsLayout />
              </Suspense>
            }
          >
            <Route index element={<Suspense fallback={null}><OpsDashboard /></Suspense>} />
            <Route path="methodologies" element={<Suspense fallback={<LoadingScreen inline message="Loading methodologies…" />}><MethodologiesDashboard /></Suspense>} />
            <Route path="methodologies/review" element={<Suspense fallback={<LoadingScreen inline message="Loading review queue…" />}><MethodologiesDashboard initialTab="review-queue" /></Suspense>} />
            <Route path="methodologies/new" element={<Suspense fallback={<LoadingScreen inline message="Loading wizard…" />}><MethodologyWizard /></Suspense>} />
            <Route path="methodologies/:seriesId" element={<Suspense fallback={<LoadingScreen inline message="Loading methodology…" />}><MethodologySeriesDetail /></Suspense>} />
            <Route path="methodologies/:seriesId/revisions/:revisionId" element={<Suspense fallback={<LoadingScreen inline message="Loading revision…" />}><MethodologyRevisionWorkspace /></Suspense>} />
            <Route path=":siteId" element={<Suspense fallback={null}><OpsDashboard /></Suspense>} />
            <Route path=":siteId/inspections/:viewName" element={<Suspense fallback={null}><OpsInspectionGridKeyed /></Suspense>} />
            <Route path=":siteId/inspections/:viewName/grid" element={<Suspense fallback={null}><OpsInspectionFullGrid /></Suspense>} />
            <Route path=":siteId/inspections/:viewName/records/:recordId" element={<Suspense fallback={null}><OpsInspectionDetail /></Suspense>} />
            <Route path=":siteId/:viewName" element={<Suspense fallback={null}><OpsGrid /></Suspense>} />
            <Route path=":siteId/records/:recordId" element={<Suspense fallback={null}><OpsRecordDetail /></Suspense>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
